import { normalizeStatus } from './modules/utils.js';
import { STATUS_COLORS, CTX_TAGS } from './modules/state.js';
import { loadOverrides } from './modules/storage.js';
import { parseContext } from './modules/utils.js';

// ── Focus trap utility for modals ──
export function trapFocus(el) {
  const focusable = el.querySelectorAll('a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])');
  if (!focusable.length) return () => {};
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  function handler(e) {
    if (e.key !== 'Tab') return;
    if (e.shiftKey) {
      if (document.activeElement === first) { e.preventDefault(); last.focus(); }
    } else {
      if (document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  }
  el.addEventListener('keydown', handler);
  return () => el.removeEventListener('keydown', handler);
}

// ── Password strength (mirrors server-side isStrongPassword) ──
export function clientIsStrongPassword(pw) {
  if (pw.length < 8) return false;
  if (pw.length >= 10) return true;
  const types = [/[A-Z]/, /[a-z]/, /[0-9]/, /[^A-Za-z0-9]/];
  return types.filter(r => r.test(pw)).length >= 3;
}

export function getPasswordStrength(pw) {
  if (!pw) return { level: 0, label: '', color: '' };
  if (pw.length < 8) return { level: 1, label: 'Too short', color: '#f7768e' };
  const types = [/[A-Z]/, /[a-z]/, /[0-9]/, /[^A-Za-z0-9]/];
  const count = types.filter(r => r.test(pw)).length;
  if (pw.length >= 10 && count >= 3) return { level: 4, label: 'Strong', color: '#9ece6a' };
  if (pw.length >= 10) return { level: 3, label: 'Good', color: '#9ece6a' };
  if (count >= 3) return { level: 3, label: 'Good', color: '#e0af68' };
  return { level: 2, label: 'Weak — add uppercase, numbers, or symbols', color: '#f7768e' };
}

// ── Collect all @tags in use across the board (for autocomplete) ──
export function collectBoardTags(data) {
  const tags = new Set(CTX_TAGS);
  const tagRe = /@([a-zA-Z][a-zA-Z0-9_-]*)/g;
  function scanTasks(tasks) { if (!tasks) return; tasks.forEach(t => { let m; tagRe.lastIndex=0; while ((m = tagRe.exec(t.text||'')) !== null) tags.add(m[1].toLowerCase()); }); }
  function scanCards(cards) { if (!cards) return; cards.forEach(c => { scanTasks(c.tasks); scanCards(c.children||[]); }); }
  if (data) { (data.columns||[]).forEach(col => scanCards(col.projects||[])); }
  const ov = loadOverrides();
  Object.values(ov.addedTasks||{}).forEach(arr => scanTasks(arr));
  return [...tags].sort();
}

// ── Collect all @tags in use across the board ──
export function collectAllContextTags(data) {
  const tags = new Set();
  function walk(cards) {
    for (const card of cards) {
      for (const t of (card.tasks || [])) {
        const { context } = parseContext(t.text);
        if (context) tags.add(context);
      }
      if (card.children) walk(card.children);
    }
  }
  for (const col of (data.columns || [])) walk(col.projects || []);
  return [...tags].sort();
}

export function getStatusStyle(status) {
  const normalized = normalizeStatus(status);
  const c = STATUS_COLORS[normalized] || STATUS_COLORS[status];
  if (c) return `background:${c.bg};color:${c.color};border-color:${c.color}40`;
  return 'background:rgba(122,162,247,0.15);color:#7aa2f7;border-color:rgba(122,162,247,0.25)';
}
